class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    this.arrows = Array.from(this.querySelectorAll('.announcement-bar__arrow'));
    this.countdowns = Array.from(this.querySelectorAll('.announcement-bar__countdown'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5;
    this.elapsed = 0;
    this.paused = false;

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1));

    // Pause rotation while the visitor interacts with the bar or a block is selected in the theme editor
    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));
    this.addEventListener('shopify:block:select', (event) => {
      this.paused = true;
      const slide = event.target.closest('.announcement-bar__slide');
      if (this.liveSlides.includes(slide)) this.show(slide);
    });
    this.addEventListener('shopify:block:deselect', () => (this.paused = false));

    // A slide that has finished leaving goes back to its resting position below the bar
    this.slides.forEach((slide) => {
      if (!slide.classList.contains('is-active')) slide.setAttribute('aria-hidden', 'true');
      slide.addEventListener('transitionend', () => {
        if (!slide.classList.contains('is-active')) slide.classList.remove('is-leaving');
      });
    });

    if (this.classList.contains('announcement-bar--sticky')) {
      // Lets the sticky header (when enabled) sit below the sticky bar
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--announcement-bar-height', `${this.offsetHeight}px`);
      });
      this.resizeObserver.observe(this);
    }

    this.update();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--announcement-bar-height');
  }

  tick() {
    this.update();
    if (!this.autoplay || this.paused || this.liveSlides.length < 2) return;

    this.elapsed += 1;
    if (this.elapsed >= this.autoplaySpeed) this.step(1);
  }

  // Re-checks the schedule in the browser, so cached pages still start and stop announcements on time
  update() {
    const now = Date.now() / 1000;

    this.liveSlides = this.slides.filter((slide) => {
      const start = Number(slide.dataset.start);
      const end = Number(slide.dataset.end);
      return (!start || now >= start) && (!end || now <= end);
    });
    this.slides.forEach((slide) => (slide.hidden = !this.liveSlides.includes(slide)));
    this.arrows.forEach((arrow) => (arrow.hidden = this.liveSlides.length < 2));
    this.hidden = this.liveSlides.length === 0;

    if (!this.liveSlides.includes(this.activeSlide)) this.show(this.liveSlides[0]);

    this.countdowns.forEach((countdown) => this.updateCountdown(countdown, now));
  }

  updateCountdown(countdown, now) {
    const remaining = Math.floor(Number(countdown.dataset.deadline) - now);
    countdown.hidden = now < Number(countdown.dataset.countdownFrom) || remaining <= 0;
    if (countdown.hidden) return;

    const values = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };
    Object.entries(values).forEach(([unit, value]) => {
      countdown.querySelector(`[data-countdown="${unit}"]`).textContent =
        unit === 'days' ? value : String(value).padStart(2, '0');
    });
  }

  get activeSlide() {
    return this.slides.find((slide) => slide.classList.contains('is-active'));
  }

  step(direction) {
    const index = this.liveSlides.indexOf(this.activeSlide);
    const count = this.liveSlides.length;
    this.show(this.liveSlides[(index + direction + count) % count]);
  }

  show(slide) {
    this.elapsed = 0;
    const current = this.activeSlide;
    if (!slide || slide === current) return;

    if (current) {
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
    }
    slide.classList.add('is-active');
    slide.removeAttribute('aria-hidden');
  }
}

customElements.define('announcement-bar', AnnouncementBar);
